import {FormProvider, useFieldArray, useForm, useWatch} from "react-hook-form";
import React, {useEffect, useMemo, useState} from "react";
import {ModalPanelForm} from "@components/Modal2023";
import FormButtons, {TraceManager} from "@components/FormButtons";
import {CausaliGiornata, PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";

import {GiustificativoRecord, GruppoEventi} from "@commons/mapping/GiustificativoRecord";
import BannerDay from "./BannerDay";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {CausaleWFRecord} from "@commons/mapping/CausaleWFRecord";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {EditGiustificativi} from "../../comps/EditGiustificativi";
import {GiuFormValues, PresenzeGiustificativoEx, setupDefaultValues} from "../../localMapping/PresenzeGiustificativoEx";

interface EditGiuProps {
    lavoratore: LavoratorePresenzeRecord,
    day: PresenzeGiornataPlusRecord,
    giustificativo?: GiustificativoRecord,
    turni: TurnoRecord[],
    giustificativi: GiustificativoRecord[],
    gruppiEventi?: GruppoEventi,
    causaliWF?: CausaleWFRecord[],
    onSave: (day: PresenzeGiornataPlusRecord, values: CausaliGiornata) => void,
    onCancelEdit: () => void,
}

export const EditGiu: React.FC<EditGiuProps> = ({
                                                    lavoratore,
                                                    day,
                                                    giustificativo: giuTrascinato,
                                                    turni,
                                                    giustificativi,
                                                    gruppiEventi,
                                                    causaliWF, onSave
                                                    ,
                                                    onCancelEdit
                                                }) => {
    const [giustificativoTrascinato, setGiustificativoTrascinato] = useState<GiustificativoRecord | null>(giuTrascinato || null);
    const traceManager = useMemo<TraceManager>(() => {
        return {
            getDeleteInfo: (trace: PresenzeGiustificativoEx) => {
                return undefined;
            }
        }
    }, []);

    // useForm<FormValues>
    const hook = useForm<GiuFormValues>({
        defaultValues: setupDefaultValues(day, giustificativi),
        mode: 'onChange',
        reValidateMode: 'onChange'
    });
    const {
        control, handleSubmit
    } = hook;

    // useFieldArray permette di gestire form dinamici sulla base di un array di oggetti
    // vedi: https://www.youtube.com/watch?v=4MrbfGSFY2A
    const useField = useFieldArray<GiuFormValues, "giustificativiHHGiornata" | "giustificativiQIGiornata">(
        {
            name: 'giustificativiHHGiornata',
            control,
            rules: {validate: () => true}
        });
    const {append, update} = useField;

    // watch per array giustificativi
    // la versione watch('giustificativiHHGiornata') NON va usata per gli array!!!
    const giustificativiHHGiornata = useWatch({
        name: 'giustificativiHHGiornata',
        control
    });

    // Inizializzo valori form con giustificativo appena trascinato
    useEffect(() => {
        const maxNumGiustificativi = PresenzeGiustificativo.maxNumGiustificativi;
        if (giustificativoTrascinato) {
            console.log("Allineo form con giustificativo appena trascinato");
            const newGiust = PresenzeGiustificativoEx.factory({codGiu: giustificativoTrascinato.codGiu!, tipoGiu: GiustificativoRecord.getTipoGiu(giustificativoTrascinato)});
            newGiust.giustificativo = giustificativi.find(v => v.codGiu!.toUpperCase() === giustificativoTrascinato.codGiu!.toUpperCase()) || null;
            let newIndex: number | null = null;
            giustificativiHHGiornata.forEach((giu, index) => {
                if (!giu.codGiu && newIndex === null) {
                    newIndex = index;
                }
            });
            if (newIndex === 0) {
                if (giustificativoTrascinato.tipoOreGiu === 'M') {
                    const timb = (day.previste && day.previste.length > 0) ? day.previste.filter(coppia => !CoppiaTimbrature.isNull(coppia)) : [];
                    if (timb.length === 1) {
                        newGiust.oraInizio = timb[0].entrata || '0000';
                        newGiust.oraFine = timb[0].uscita || '0000';
                        newGiust.quantita = RiepilogoOrePlus.getTotaleHPrev(day.totali) / 60.0;
                    }
                }
                update(newIndex, newGiust);
            } else if (giustificativiHHGiornata.length < maxNumGiustificativi) {
                append(newGiust);
            }
            setGiustificativoTrascinato(null);
        }
    }, [day, giustificativiHHGiornata, giustificativoTrascinato, giustificativi, update, append]);

    //console.log("ModalPanelForm giustificativi");
    return <ModalPanelForm isOpen={!!day} formControl={control} handleDismiss={onCancelEdit}
                           header={<BannerDay day={day} lavoratore={lavoratore} turni={turni} giustificativi={giustificativi} name="Pannello Giustificativi"
                                              anomalia={day.anomalia}
                                              page='Pianificazione'/>}>
        <FormProvider {...hook}>
            <form className="p-2 bg-white shadow-md rounded xl:min-w-[1130px]"
                  onSubmit={handleSubmit(values => onSave(day, values.giustificativiHHGiornata.map(v => {
                      return {...v, giustificativo: undefined}
                  })))}>
                <div className="min-h-[480px] flex flex-col">
                    <div className="mb-auto">
                        <EditGiustificativi lavoratore={lavoratore} day={day} giustificativi={giustificativi} gruppiEventi={gruppiEventi} useFieldArrayReturn={useField}
                                            name="giustificativiHHGiornata"/>
                    </div>
                    <div className="">
                        <FormButtons onCancel={onCancelEdit} resource={day} traceManager={traceManager}/>
                    </div>
                </div>
            </form>
        </FormProvider>
    </ModalPanelForm>
}
export default EditGiu;